import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Nango from '@nangohq/frontend';
import { AlertTriangle, Cable, CheckCircle2, ExternalLink, LockKeyhole, RefreshCw } from 'lucide-react';
import SEO from '../components/SEO';
import { toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  connected: 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  error: 'border border-rose-400/20 bg-rose-500/10 text-rose-200',
  expired: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
  disconnected: 'border border-border-subtle bg-card/40 text-text-muted',
};

export default function AuthLinkAuthorizationPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState(null);
  const [pageError, setPageError] = useState('');

  const loadLink = async () => {
    try {
      setLoading(true);
      setPageError('');
      const res = await fetch(`${API}/public/auth-links/${token}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo validar el enlace');
      setData(json);
    } catch (error) {
      setPageError(error.message || 'No se pudo validar el enlace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLink();
  }, [token]);

  const connectProvider = async (providerKey) => {
    try {
      setBusyProvider(providerKey);
      const res = await fetch(`${API}/public/auth-links/${token}/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: providerKey }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo abrir la autorización');

      const nango = new Nango();
      const connectUi = nango.openConnectUI({
        baseURL: json.nango_connect_ui_base_url,
        apiURL: json.nango_api_base_url,
        lang: 'es',
        themeOverride: 'dark',
        detectClosedAuthWindow: true,
        onEvent: async (event) => {
          if (event.type === 'connect') {
            toast.success('Conexión autorizada correctamente');
            await loadLink();
          }
          if (event.type === 'error') {
            toast.error(event.payload?.errorMessage || 'La autorización falló');
            await loadLink();
          }
          if (event.type === 'close') {
            await loadLink();
          }
        }
      });
      connectUi.setSessionToken(json.session_token);
    } catch (error) {
      toast.error(error.message || 'No se pudo iniciar la autorización');
    } finally {
      setBusyProvider(null);
    }
  };

  const connectAll = async () => {
    const candidate = (data?.providers || []).find((item) => item.can_connect && item.status !== 'connected' && !item.coming_soon);
    if (!candidate) {
      toast.error('No hay conexiones disponibles para autorizar ahora mismo');
      return;
    }
    await connectProvider(candidate.provider);
  };

  return (
    <>
      <SEO title="Autorizar conexiones — Neuralia369" description="Autoriza tus conexiones con Neuralia369 de forma segura y sin compartir contraseñas." />
      <div className="min-h-screen bg-background pt-28 pb-20" data-testid="auth-link-authorization-page">
        <div className="mx-auto max-w-6xl px-4 space-y-6">
          <div className="glass-card tracing-panel overflow-hidden" data-testid="auth-link-hero-card">
            <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
              <div>
                <p className="editorial-kicker mb-3">Neuralia369</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Autorización segura de conexiones</p>
                <h1 className="mt-4 text-4xl font-semibold text-text-heading">Conecta tus herramientas sin compartir contraseñas.</h1>
                <p className="mt-4 max-w-3xl text-sm text-text-body">{data?.copy_blocks?.subheadline || 'Validamos el acceso y te mostramos solo los permisos necesarios en lenguaje simple.'}</p>
                {data?.client && (
                  <div className="mt-5 rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid="auth-link-client-card">
                    <p className="text-sm font-semibold text-text-heading">Cliente: {data.client.name || data.client.email}</p>
                    <p className="mt-2 text-xs text-text-muted">{data.client.email}{data.client.company ? ` · ${data.client.company}` : ''}</p>
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="glass-card p-5" data-testid="auth-link-status-card">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado del enlace</p>
                  <p className="mt-3 text-2xl font-bold text-text-heading">{data?.link?.status || 'pendiente'}</p>
                  <p className="mt-2 text-xs text-text-muted">Expira: {data?.link?.expires_at ? new Date(data.link.expires_at).toLocaleString('es-ES') : '—'}</p>
                </div>
                <div className="glass-card p-5" data-testid="auth-link-progress-card">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Progreso</p>
                  <p className="mt-3 text-2xl font-bold text-text-heading">{data?.link?.authorized_providers?.length || 0}/{data?.providers?.length || 0}</p>
                  <p className="mt-2 text-xs text-text-muted">conexiones autorizadas</p>
                </div>
                <div className="glass-card p-5 md:col-span-2" data-testid="auth-link-security-card">
                  <div className="flex items-center gap-3 text-text-heading"><LockKeyhole className="h-5 w-5 text-primary" /><p className="text-sm font-semibold">Qué ocurre aquí</p></div>
                  <div className="mt-3 space-y-2 text-sm text-text-body">
                    <p>• No compartes contraseñas con Neuralia369.</p>
                    <p>• Tú autorizas directamente el acceso a cada herramienta.</p>
                    <p>• Podrás revocar cada conexión cuando quieras desde tu panel.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {pageError && (
            <div className="rounded-[24px] border border-rose-400/20 bg-rose-500/10 p-5 text-sm text-rose-100" data-testid="auth-link-error-card">
              <div className="flex items-center gap-3"><AlertTriangle className="h-5 w-5" /><span>{pageError}</span></div>
            </div>
          )}

          {!pageError && data?.link?.all_connected && (
            <div className="rounded-[24px] border border-emerald-400/20 bg-emerald-500/10 p-5 text-sm text-emerald-100" data-testid="auth-link-success-card">
              <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5" /><span>Todo listo. Las conexiones de este enlace ya quedaron autorizadas.</span></div>
            </div>
          )}

          {!pageError && (
            <div className="glass-card p-6" data-testid="auth-link-actions-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Orden recomendado</p>
                  <h2 className="mt-2 text-xl font-semibold text-text-heading">{data?.copy_blocks?.cta_group || 'Conecta tus herramientas siguiendo el orden sugerido.'}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={loadLink} data-testid="auth-link-refresh-button"><RefreshCw className="h-4 w-4" /> Refrescar</button>
                  <button type="button" className="btn-primary" onClick={connectAll} data-testid="auth-link-connect-all-button"><Cable className="h-4 w-4" /> Conectar todo</button>
                </div>
              </div>
            </div>
          )}

          {!pageError && (
            <div className="grid gap-4 xl:grid-cols-3" data-testid="auth-link-provider-grid">
              {(data?.providers || []).map((provider) => (
                <div key={provider.provider} className="glass-card p-6" data-testid={`auth-link-provider-card-${provider.provider}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Conexión</p>
                      <h3 className="mt-2 text-lg font-semibold text-text-heading">{provider.name}</h3>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[provider.status] || STATUS_STYLES.disconnected}`} data-testid={`auth-link-provider-status-${provider.provider}`}>{provider.status}</span>
                  </div>
                  <p className="mt-3 text-sm text-text-body">{provider.description}</p>
                  <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`auth-link-provider-consent-${provider.provider}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Texto de consentimiento</p>
                    <p className="mt-2 text-sm text-text-body">{provider.consent_text}</p>
                  </div>
                  <div className="mt-4 rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`auth-link-provider-permissions-${provider.provider}`}>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Permisos en lenguaje simple</p>
                    <div className="mt-2 space-y-2 text-sm text-text-body">
                      {(provider.plain_permissions || []).map((item, index) => <div key={`${provider.provider}-${index}`}>• {item}</div>)}
                    </div>
                  </div>
                  <p className="mt-4 text-xs text-text-muted" data-testid={`auth-link-provider-helper-${provider.provider}`}>{provider.helper}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button type="button" className="btn-primary" onClick={() => connectProvider(provider.provider)} disabled={busyProvider === provider.provider || provider.coming_soon || !provider.can_connect || provider.status === 'connected'} data-testid={`auth-link-provider-connect-${provider.provider}`}>
                      <Cable className="h-4 w-4" />
                      {provider.status === 'connected' ? 'Conectado' : (busyProvider === provider.provider ? 'Abriendo...' : 'Conectar')}
                    </button>
                    {provider.status === 'connected' && <a href="/contacto" className="btn-secondary" data-testid={`auth-link-provider-contact-${provider.provider}`}><ExternalLink className="h-4 w-4" /> Continuar</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}